import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Col, Row, Form, Button, Label, Input, Table } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Header from "../../../layout/Header/Header";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { FiltroTipoHabilitacion, coincideTipo } from "../Habilitacion/TiposHabilitacion";

/**
 * Traspaso de tela y habilitacion entre almacenes (bodega ↔ maquilero).
 *
 * A diferencia de las pantallas viejas, aqui NO se encadenan llamadas desde el
 * navegador: se manda un solo POST con la cabecera y todos los renglones, y el
 * back lo aplica dentro de una transaccion. Si a un material no le alcanza, no
 * se mueve nada y la respuesta trae el faltante por material y almacen.
 */
function TraspasosMaterialesCreate() {
  const { user } = useContext(AuthContext);
  const URL_TRASPASOS_MATERIALES = process.env.REACT_APP_URL_TRASPASOS_MATERIALES;
  const URL_INVENTARIOS_MATERIALES = process.env.REACT_APP_URL_INVENTARIOS_MATERIALES;
  const URL_AREAS = process.env.REACT_APP_URL_AREAS;
  const URL_TELAS = process.env.REACT_APP_URL_TELAS;
  const URL_HABILITACION = process.env.REACT_APP_URL_HABILITACION;
  const URL_COLORES = process.env.REACT_APP_URL_COLORES;
  const URL_CORTES = process.env.REACT_APP_URL_CORTES;
  const URL_REQUISICION = process.env.REACT_APP_URL_REQUISICION;

  const hoy = moment(new Date()).format("YYYY-MM-DD");

  const [fecha, setFecha] = useState(hoy);
  const [areaSalida, setAreaSalida] = useState("");
  const [areaDestino, setAreaDestino] = useState("");
  const [observaciones, setObservaciones] = useState("NA");
  const [validaBoton, setValidaBoton] = useState(true);

  const [areas, setAreas] = useState([]);
  const [telas, setTelas] = useState([]);
  const [habilitacion, setHabilitacion] = useState([]);
  const [colores, setColores] = useState([]);
  const [existencias, setExistencias] = useState([]);
  // Orden de maquila para la que se manda el material (opcional).
  const [ordenes, setOrdenes] = useState([]);
  const [selectedCorte, setSelectedCorte] = useState("");
  const [filtroTipoHab, setFiltroTipoHab] = useState("");
  const [requisicion, setRequisicion] = useState(null);

  const [inputFields, setInputFields] = useState([
    { id: uuidv4(), tipoMaterial: "Habilitacion", material: "", colores: "", cantidad: 0 },
  ]);

  const headers = { Authorization: `Bearer: ${localStorage.getItem("app_token")}` };

  useEffect(() => {
    const pide = (url, set, transforma = (d) => d) =>
      axios
        .get(url, { headers })
        .then((res) => set(transforma(res.data)))
        .catch((err) => console.log(err));

    pide(URL_AREAS, setAreas);
    pide(URL_TELAS, setTelas);
    pide(URL_HABILITACION, setHabilitacion);
    pide(URL_COLORES, setColores);
    pide(URL_CORTES, setOrdenes, (d) =>
      d.filter((c) => c.status === "Abierto" && c.is_active !== "No").sort((a, b) => b.idCorte - a.idCorte)
    );
  }, []);

  // Al elegir la orden, el destino es el almacen de su maquilero y se trae lo
  // que pide la orden contra lo que ya se le mando.
  const eligeOrden = (id, prellenar = false) => {
    setSelectedCorte(id);
    setRequisicion(null);
    if (!id) return;
    axios
      .get(`${URL_REQUISICION}Orden/${id}`, { headers })
      .then((res) => {
        setRequisicion(res.data);
        if (res.data.almacenMaquilero) setAreaDestino(res.data.almacenMaquilero._id);
        if (!areaSalida) {
          const bodega = areas.find((a) => a.tipo !== "Maquilero");
          if (bodega) setAreaSalida(bodega._id);
        }
        if (prellenar) llenarCon(res.data);
      })
      .catch((err) =>
        Swal.fire({ icon: "error", title: "No se pudo leer la requisicion", text: err.response?.data?.message || err.message })
      );
  };

  // Desde el reporte de salida de habilitacion se llega con ?corte=<id>: la
  // orden queda elegida en cuanto cargan los almacenes.
  useEffect(() => {
    const corte = new URLSearchParams(window.location.search).get("corte");
    if (corte && areas.length && !selectedCorte) eligeOrden(corte, true);
  }, [areas]);

  const prellenarPendiente = () => llenarCon(requisicion);

  function llenarCon(req) {
    if (req?.sinExplosion) {
      return Swal.fire({
        icon: "warning",
        title: "Sin explosion",
        text: "El pedido de esta orden no tiene explosion capturada: no se sabe que material pide. Capturalo a mano o captura la explosion.",
      });
    }
    const pendientes = (req?.renglones || []).filter((r) => r.pendiente > 0);
    if (!pendientes.length) {
      return Swal.fire({ icon: "info", title: "Nada pendiente", text: "A esta orden ya se le mando todo lo que pide su explosion" });
    }
    setInputFields(
      pendientes.map((r) => ({
        id: uuidv4(),
        tipoMaterial: r.tipoMaterial,
        material: r.material,
        colores: r.colores || "",
        cantidad: r.pendiente,
      }))
    );
  }

  // Al elegir origen se traen sus saldos, para capturar contra lo que hay.
  useEffect(() => {
    if (!areaSalida) return setExistencias([]);
    axios
      .get(`${URL_INVENTARIOS_MATERIALES}/almacen/${areaSalida}`, { headers })
      .then((res) => setExistencias(res.data))
      .catch((err) => console.log(err));
  }, [areaSalida]);

  /** Lo apartado para pedidos de ese material y color en el origen. */
  const apartadoDe = (renglon) => {
    const saldo = existencias.find(
      (e) => e.material && e.material._id === renglon.material && (e.colores ? e.colores._id : "") === (renglon.colores || "")
    );
    return saldo ? saldo.apartado || 0 : 0;
  };

  /** Lo que hay en el origen de ese material y color. */
  const existenciaDe = (renglon) => {
    const saldo = existencias.find(
      (e) =>
        e.material &&
        e.material._id === renglon.material &&
        (e.colores ? e.colores._id : "") === (renglon.colores || "")
    );
    return saldo ? saldo.cantidad : 0;
  };

  const handleChangeInput = (id, campo, valor) => {
    setInputFields(
      inputFields.map((i) => {
        if (i.id !== id) return i;
        // Cambiar de tipo invalida el material elegido.
        if (campo === "tipoMaterial") return { ...i, tipoMaterial: valor, material: "", colores: "" };
        return { ...i, [campo]: valor };
      })
    );
  };

  const handleAddFields = () =>
    setInputFields([
      ...inputFields,
      { id: uuidv4(), tipoMaterial: "Habilitacion", material: "", colores: "", cantidad: 0 },
    ]);

  const handleRemoveFields = (id) =>
    setInputFields(inputFields.filter((i) => i.id !== id));

  const clear = () => {
    setObservaciones("NA");
    setSelectedCorte("");
    setRequisicion(null);
    setInputFields([
      { id: uuidv4(), tipoMaterial: "Habilitacion", material: "", colores: "", cantidad: 0 },
    ]);
  };

  const saveTraspaso = async (event) => {
    event.preventDefault();

    const renglones = inputFields
      .filter((a) => a.material !== "" && parseFloat(a.cantidad) > 0)
      .map((a) => ({
        tipoMaterial: a.tipoMaterial,
        material: a.material,
        colores: a.colores || undefined,
        cantidad: parseFloat(a.cantidad),
      }));

    if (renglones.length === 0) {
      return Swal.fire({ icon: "warning", title: "Falta capturar", text: "Agrega al menos un material con cantidad" });
    }
    if (areaSalida === areaDestino) {
      return Swal.fire({ icon: "warning", title: "Revisa los almacenes", text: "El origen y el destino no pueden ser el mismo" });
    }

    setValidaBoton(false);
    try {
      await axios.post(
        URL_TRASPASOS_MATERIALES,
        { fecha, areaSalida, areaDestino, observaciones, renglones, cortes: selectedCorte || undefined },
        { headers }
      );
      Swal.fire({ position: "center", icon: "success", title: "Traspaso aplicado", showConfirmButton: false, timer: 2000 });
      clear();
      setTimeout(() => {
        window.location.href = "/ListadoTraspasosMateriales";
      }, 1200);
    } catch (error) {
      // El back devuelve el detalle de faltantes; se muestra tal cual para que
      // el usuario sepa que material y en que almacen quedo corto.
      const data = error.response ? error.response.data : {};
      const detalle = (data.faltantes || [])
        .map((f) => `${f.material}${f.color ? ` (${f.color})` : ""} en ${f.almacen}: faltan ${f.faltante}`)
        .join("<br/>");
      Swal.fire({
        icon: "error",
        title: "No se pudo aplicar el traspaso",
        html: detalle || data.message || "Algo salio mal",
      });
      setValidaBoton(true);
    }
  };

  // Con filtro de tipo, la habilitacion ya elegida en el renglon se sigue viendo.
  const materialesDe = (tipo, elegido) =>
    tipo === "Telas" ? telas : habilitacion.filter((m) => m._id === elegido || coincideTipo(m, filtroTipoHab));

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_telas ? (
        <div className="card container col-12">
          <div className="page-header">
            <div>
              <h3 className="page-title">
                <i className="fas fa-exchange-alt" style={{ marginRight: 8, color: "#0ea5e9" }} />
                Nuevo Traspaso de Material
              </h3>
              <p className="page-subtitle">Mueve tela y habilitacion entre bodega y maquileros</p>
            </div>
          </div>

          <Form onSubmit={saveTraspaso}>
            <div className="form-section">
              <div className="form-section-header">
                <i className="fas fa-info-circle" />
                <span className="form-section-title">Datos Generales</span>
              </div>
              <div className="form-section-body">
                <Row>
                  <Col md={6}>
                    <Label>Orden de Maquila (opcional)</Label>
                    <Input type="select" value={selectedCorte} onChange={(e) => eligeOrden(e.target.value)}>
                      <option value="">Sin orden</option>
                      {ordenes.map((c) => (
                        <option value={c._id} key={c._id}>
                          {c.numero || c.idCorte} · {c.proveedores?.[0]?.nombre_comercial ?? "Sin maquilero"} · Pedido{" "}
                          {c.pedidos?.[0]?.idPedido ?? ""} {c.pedidos?.[0]?.clientes?.[0]?.nombre_comercial ?? ""}
                        </option>
                      ))}
                    </Input>
                  </Col>
                </Row>
                <Row>
                  <Col md={2}>
                    <Label>Fecha</Label>
                    <Input type="date" value={fecha} required onChange={(e) => setFecha(e.target.value)} />
                  </Col>
                  <Col md={3}>
                    <Label>Almacen Origen</Label>
                    <Input type="select" value={areaSalida} required onChange={(e) => setAreaSalida(e.target.value)}>
                      <option value="">Selecciona</option>
                      {areas
                        .sort((a, b) => (a.name > b.name ? 1 : -1))
                        .map((a) => (
                          <option value={a._id} key={a._id}>
                            {a.name}
                          </option>
                        ))}
                    </Input>
                  </Col>
                  <Col md={3}>
                    <Label>Almacen Destino</Label>
                    <Input type="select" value={areaDestino} required onChange={(e) => setAreaDestino(e.target.value)}>
                      <option value="">Selecciona</option>
                      {areas
                        .filter((a) => a._id !== areaSalida)
                        .sort((a, b) => (a.name > b.name ? 1 : -1))
                        .map((a) => (
                          <option value={a._id} key={a._id}>
                            {a.name}
                          </option>
                        ))}
                    </Input>
                  </Col>
                  <Col md={4}>
                    <Label>Observaciones</Label>
                    <Input type="text" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
                  </Col>
                </Row>
              </div>
            </div>

            {requisicion ? (
              <div className="form-section">
                <div className="form-section-header">
                  <i className="fas fa-clipboard-list" />
                  <span className="form-section-title">
                    Requisicion de la orden {requisicion.corte.numero}
                  </span>
                </div>
                <div className="form-section-body">
                  {requisicion.sinExplosion ? (
                    <p className="text-muted">
                      El pedido de esta orden no tiene explosion capturada: no se puede calcular lo que pide.
                    </p>
                  ) : (
                    <>
                      <Table size="sm">
                        <thead>
                          <tr>
                            <th>Tipo</th>
                            <th>Material</th>
                            <th>Color</th>
                            <th>Por prenda</th>
                            <th>Requerido</th>
                            <th>Ya mandado</th>
                            <th>Pendiente</th>
                          </tr>
                        </thead>
                        <tbody>
                          {requisicion.renglones.map((r) => (
                            <tr key={`${r.tipoMaterial}${r.material}${r.colores}`}>
                              <td>{r.tipoMaterial === "Telas" ? "Tela" : "Habilitacion"}</td>
                              <td>{r.nombre}</td>
                              <td>{r.color}</td>
                              <td>{r.cantidadPrenda}</td>
                              <td>{r.requerido}</td>
                              <td>{r.enviado}</td>
                              <td style={{ color: r.pendiente > 0 ? "#dc2626" : "#16a34a", fontWeight: 600 }}>
                                {r.pendiente}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      <Button type="button" color="info" onClick={prellenarPendiente}>
                        <i className="fas fa-magic" style={{ marginRight: 6 }} /> Prellenar con lo pendiente
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ) : undefined}

            <div className="form-section">
              <div className="form-section-header">
                <i className="fas fa-boxes" />
                <span className="form-section-title">Materiales</span>
              </div>
              <div className="form-section-body">
                {inputFields.some((a) => a.tipoMaterial === "Habilitacion") ? (
                  <div style={{ maxWidth: 320, marginBottom: 8 }}>
                    <Label>Tipo de habilitación</Label>
                    <FiltroTipoHabilitacion bsSize="sm" value={filtroTipoHab} onChange={setFiltroTipoHab} />
                  </div>
                ) : null}
                <Table borderless>
                  <thead>
                    <tr>
                      <th style={{ width: "15%" }}>Tipo</th>
                      <th style={{ width: "30%" }}>Material</th>
                      <th style={{ width: "20%" }}>Color</th>
                      <th style={{ width: "15%" }}>Cantidad</th>
                      <th style={{ width: "12%" }}>En origen</th>
                      <th style={{ width: "8%" }} />
                    </tr>
                  </thead>
                  <tbody>
                    {inputFields.map((a) => {
                      const disponible = existenciaDe(a);
                      const excede = parseFloat(a.cantidad) > disponible;
                      return (
                        <tr key={a.id}>
                          <td>
                            <Input
                              type="select"
                              value={a.tipoMaterial}
                              onChange={(e) => handleChangeInput(a.id, "tipoMaterial", e.target.value)}
                            >
                              <option value="Habilitacion">Habilitacion</option>
                              <option value="Telas">Tela</option>
                            </Input>
                          </td>
                          <td>
                            <Input
                              type="select"
                              value={a.material}
                              required
                              onChange={(e) => handleChangeInput(a.id, "material", e.target.value)}
                            >
                              <option value="">Selecciona</option>
                              {materialesDe(a.tipoMaterial, a.material)
                                .sort((x, y) => (x.nombre > y.nombre ? 1 : -1))
                                .map((m) => (
                                  <option value={m._id} key={m._id}>
                                    {m.nombre}
                                  </option>
                                ))}
                            </Input>
                          </td>
                          <td>
                            <Input
                              type="select"
                              value={a.colores}
                              disabled={a.tipoMaterial !== "Telas"}
                              onChange={(e) => handleChangeInput(a.id, "colores", e.target.value)}
                            >
                              <option value="">
                                {a.tipoMaterial === "Telas" ? "Selecciona" : "No aplica"}
                              </option>
                              {colores
                                .sort((x, y) => (x.name > y.name ? 1 : -1))
                                .map((c) => (
                                  <option value={c._id} key={c._id}>
                                    {c.name}
                                  </option>
                                ))}
                            </Input>
                          </td>
                          <td>
                            <Input
                              type="number"
                              min="0"
                              step="0.0001"
                              value={a.cantidad}
                              required
                              onChange={(e) => handleChangeInput(a.id, "cantidad", e.target.value)}
                            />
                          </td>
                          <td>
                            <span style={{ color: excede ? "#dc2626" : "#64748b", fontWeight: excede ? 600 : 400 }}>
                              {disponible}
                            </span>
                            {apartadoDe(a) > 0 ? (
                              <div style={{ fontSize: 11, color: "#8b5cf6" }}>{apartadoDe(a)} apartado</div>
                            ) : undefined}
                          </td>
                          <td>
                            <Button title="Quitar renglón"
                              size="sm"
                              className="btn"
                              color="danger"
                              disabled={inputFields.length === 1}
                              onClick={() => handleRemoveFields(a.id)}
                            >
                              <i className="fas fa-minus" />
                            </Button>
                            <Button title="Agregar renglón" size="sm" className="btn" color="info" onClick={handleAddFields}>
                              <i className="fas fa-plus" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </div>

            <div className="submit-bar">
              <Button href="/ListadoTraspasosMateriales" className="btn btn-danger" id="botonListado">
                Regresar
              </Button>
              <Button type="submit" className="btn btn-success" disabled={!validaBoton}>
                <i className="fas fa-save" style={{ marginRight: 6 }} /> Guardar
              </Button>
            </div>
          </Form>
          <br />
        </div>
      ) : undefined}
    </>
  );
}

export default TraspasosMaterialesCreate;
